import { useState } from "react";
import { DefaultButton, IconButton, Dialog, DialogType, DialogFooter, PrimaryButton } from "@fluentui/react";

import mobileStyles from "./ChatMobile.module.css";

import { Answer, AnswerError, AnswerLoading } from "../../components/Answer";
import { QuestionInput } from "../../components/QuestionInput";
import { ExampleList } from "../../components/Example";
import { UserChatMessage } from "../../components/UserChatMessage";
import { AnalysisPanel, AnalysisPanelTabs } from "../../components/AnalysisPanel";
import { ClearChatButton } from "../../components/ClearChatButton";
import { Settings } from "../../components/Settings/Settings";
import { ClinicalSettings } from "../../components/ClinicalSettings/ClinicalSettings";
import { TokenBudgetIndicator } from "../../components/TokenBudgetIndicator";
import { TokenClaimsDisplay } from "../../components/TokenClaimsDisplay";

import { useChatSession } from "../../hooks/useChatSession";

const ChatMobile = () => {
    const s = useChatSession({ defaultContentMode: "full", compact: true });
    const [showSettings, setShowSettings] = useState(false);
    const [showClinical, setShowClinical] = useState(false);
    const selectedAnalysisAnswer = s.answers[s.selectedAnswer]?.[1];

    return (
        <div className={mobileStyles.container}>
            {/* Compact toolbar */}
            <div className={mobileStyles.toolbar}>
                <div className={mobileStyles.toolbarGroup}>
                    <ClearChatButton onClick={s.clearChat} disabled={!s.lastQuestionRef.current || s.isLoading} />
                </div>
                <div className={mobileStyles.toolbarGroup}>
                    <IconButton iconProps={{ iconName: "PlayerSettings" }} title="Clinical Settings" onClick={() => setShowClinical(true)} />
                    <IconButton iconProps={{ iconName: "Settings" }} title="Developer Settings" onClick={() => setShowSettings(true)} />
                </div>
            </div>

            {/* Message area */}
            <div className={mobileStyles.messageArea} ref={s.chatMessageStreamContainer} onScroll={s.onChatMessageStreamScroll}>
                {!s.lastQuestionRef.current ? (
                    <div className={mobileStyles.emptyState}>
                        <h1 className={mobileStyles.emptyStateTitle}>
                            {s.clinicalFilters.patientName ? <>{s.clinicalFilters.patientName} &mdash; Clinical Notes</> : s.t("chatEmptyStateTitle")}
                        </h1>
                        {s.useBudgetMonitor && (
                            <TokenBudgetIndicator
                                estimate={s.tokenEstimate}
                                isLoading={s.isEstimatingTokens}
                                compact={true}
                                onOptionSelect={s.handleBudgetOptionSelect}
                                onOpenDeveloperSettings={() => setShowSettings(true)}
                                onOpenClinicalSettings={() => setShowClinical(true)}
                            />
                        )}
                        <h2 className={mobileStyles.emptyStateSubtitle}>{s.t("chatEmptyStateSubtitle")}</h2>
                        <ExampleList onExampleClicked={s.onExampleClicked} useMultimodalAnswering={s.showMultimodalOptions} />
                    </div>
                ) : (
                    <>
                        {s.isStreaming &&
                            s.streamedAnswers.map((streamedAnswer, index) => (
                                <div key={index}>
                                    <UserChatMessage message={streamedAnswer[0]} />
                                    <div className={mobileStyles.messageGpt}>
                                        <Answer
                                            isStreaming={true}
                                            key={index}
                                            answer={streamedAnswer[1]}
                                            index={index}
                                            speechConfig={s.speechConfig}
                                            isSelected={false}
                                            onCitationClicked={c => s.onShowCitation(c, index)}
                                            onThoughtProcessClicked={() => s.onToggleTab(AnalysisPanelTabs.ThoughtProcessTab, index)}
                                            onSupportingContentClicked={() => s.onToggleTab(AnalysisPanelTabs.SupportingContentTab, index)}
                                            onEvidenceGraphClicked={() => s.onToggleTab(AnalysisPanelTabs.EvidenceGraphTab, index)}
                                            onFollowupQuestionClicked={q => s.makeApiRequest(q)}
                                            showFollowupQuestions={s.useSuggestFollowupQuestions && s.streamedAnswers.length - 1 === index}
                                            showSpeechOutputAzure={s.showSpeechOutputAzure}
                                            showSpeechOutputBrowser={s.showSpeechOutputBrowser}
                                            compact
                                        />
                                    </div>
                                </div>
                            ))}
                        {!s.isStreaming &&
                            s.answers.map((answer, index) => (
                                <div key={index}>
                                    <UserChatMessage message={answer[0]} />
                                    <div className={mobileStyles.messageGpt}>
                                        <Answer
                                            isStreaming={false}
                                            key={index}
                                            answer={answer[1]}
                                            index={index}
                                            speechConfig={s.speechConfig}
                                            isSelected={s.selectedAnswer === index && s.activeAnalysisPanelTab !== undefined}
                                            onCitationClicked={c => s.onShowCitation(c, index)}
                                            onThoughtProcessClicked={() => s.onToggleTab(AnalysisPanelTabs.ThoughtProcessTab, index)}
                                            onSupportingContentClicked={() => s.onToggleTab(AnalysisPanelTabs.SupportingContentTab, index)}
                                            onEvidenceGraphClicked={() => s.onToggleTab(AnalysisPanelTabs.EvidenceGraphTab, index)}
                                            onFollowupQuestionClicked={q => s.makeApiRequest(q)}
                                            showFollowupQuestions={s.useSuggestFollowupQuestions && s.answers.length - 1 === index}
                                            showSpeechOutputAzure={s.showSpeechOutputAzure}
                                            showSpeechOutputBrowser={s.showSpeechOutputBrowser}
                                            compact
                                        />
                                    </div>
                                </div>
                            ))}
                        {s.isLoading && (
                            <>
                                <UserChatMessage message={s.lastQuestionRef.current} />
                                <div className={mobileStyles.messageGptMinWidth}>
                                    <AnswerLoading />
                                </div>
                            </>
                        )}
                        {s.error ? (
                            <>
                                <UserChatMessage message={s.lastQuestionRef.current} />
                                <div className={mobileStyles.messageGptMinWidth}>
                                    <AnswerError error={s.error.toString()} onRetry={() => s.makeApiRequest(s.lastQuestionRef.current)} />
                                </div>
                            </>
                        ) : null}
                        <div ref={s.chatMessageStreamEnd} />
                    </>
                )}
            </div>

            {/* Sticky input at bottom */}
            <div className={mobileStyles.inputBar}>
                <QuestionInput
                    clearOnSend
                    placeholder={s.t("defaultExamples.placeholder")}
                    disabled={s.isLoading}
                    onSend={question => s.makeApiRequest(question)}
                    showSpeechInput={s.showSpeechInput}
                />
            </div>

            {/* Analysis drawer (bottom sheet) */}
            {selectedAnalysisAnswer && s.activeAnalysisPanelTab && (
                <div className={mobileStyles.analysisDrawer}>
                    <div style={{ textAlign: "right", flexShrink: 0 }}>
                        <IconButton iconProps={{ iconName: "Cancel" }} onClick={() => s.onToggleTab(s.activeAnalysisPanelTab!, s.selectedAnswer)} />
                    </div>
                    <div className={mobileStyles.analysisDrawerContent}>
                        <AnalysisPanel
                            className=""
                            activeCitation={s.activeCitation}
                            onActiveTabChanged={x => s.onToggleTab(x, s.selectedAnswer)}
                            citationHeight="40vh"
                            answer={selectedAnalysisAnswer}
                            activeTab={s.activeAnalysisPanelTab}
                            onCitationClicked={c => s.onShowCitation(c, s.selectedAnswer)}
                        />
                    </div>
                </div>
            )}

            {/* Settings full-screen overlay */}
            {showSettings && (
                <div className={mobileStyles.settingsOverlay}>
                    <div className={mobileStyles.settingsHeader}>
                        <h2>{s.t("labels.headerText")}</h2>
                        <IconButton iconProps={{ iconName: "Cancel" }} onClick={() => setShowSettings(false)} />
                    </div>
                    <Settings
                        promptTemplate={s.promptTemplate}
                        temperature={s.temperature}
                        retrieveCount={s.clinicalFilters.topResults}
                        agenticReasoningEffort={s.agenticReasoningEffort}
                        seed={s.seed}
                        minimumSearchScore={s.minimumSearchScore}
                        minimumRerankerScore={s.minimumRerankerScore}
                        useSemanticRanker={s.useSemanticRanker}
                        useSemanticCaptions={s.contentMode === "semantic"}
                        useQueryRewriting={s.useQueryRewriting}
                        reasoningEffort={s.reasoningEffort}
                        excludeCategory={s.excludeCategory}
                        includeCategory={s.includeCategory}
                        retrievalMode={s.retrievalMode}
                        showMultimodalOptions={s.showMultimodalOptions}
                        sendTextSources={s.sendTextSources}
                        sendImageSources={s.sendImageSources}
                        searchTextEmbeddings={s.searchTextEmbeddings}
                        searchImageEmbeddings={s.searchImageEmbeddings}
                        showSemanticRankerOption={s.showSemanticRankerOption}
                        showQueryRewritingOption={s.showQueryRewritingOption}
                        showReasoningEffortOption={s.showReasoningEffortOption}
                        showVectorOption={s.showVectorOption}
                        useLogin={s.useLogin}
                        loggedIn={s.loggedIn}
                        requireAccessControl={s.requireAccessControl}
                        shouldStream={s.shouldStream}
                        streamingEnabled={s.streamingEnabled}
                        useSuggestFollowupQuestions={s.useSuggestFollowupQuestions}
                        showAgenticRetrievalOption={s.showAgenticRetrievalOption}
                        useAgenticKnowledgeBase={s.useAgenticKnowledgeBase}
                        useBudgetMonitor={s.useBudgetMonitor}
                        contextPerspective={s.contextPerspective}
                        retrievalEffort={s.retrievalEffort}
                        useLangGraphClinicalRetrieval={s.useLangGraphClinicalRetrieval}
                        showGraphSupplementalEvidenceOption={s.showGraphSupplementalEvidenceOption}
                        showGraphSupplementalEvidence={s.showGraphSupplementalEvidence}
                        useWebSource={s.webSourceEnabled}
                        showWebSourceOption={s.webSourceSupported}
                        useSharePointSource={s.sharePointSourceEnabled}
                        showSharePointSourceOption={s.sharePointSourceSupported}
                        hideMinimalRetrievalReasoningOption={s.hideMinimalRetrievalReasoningOption}
                        availableModels={s.availableModels}
                        selectedModel={s.selectedModel}
                        selectedTemplate={s.selectedTemplate}
                        onModelChange={model => s.handleSettingsChange("selectedModel", model)}
                        onTemplateChange={template => s.handleSettingsChange("selectedTemplate", template)}
                        page="chat"
                        onChange={s.handleSettingsChange}
                    />
                    {s.useLogin && <TokenClaimsDisplay />}
                </div>
            )}

            {/* Clinical settings full-screen overlay */}
            {showClinical && (
                <div className={mobileStyles.settingsOverlay}>
                    <div className={mobileStyles.settingsHeader}>
                        <h2>Clinical Settings</h2>
                        <IconButton iconProps={{ iconName: "Cancel" }} onClick={() => setShowClinical(false)} />
                    </div>
                    <ClinicalSettings
                        filters={s.clinicalFilters}
                        onFiltersChange={newFilters => {
                            const facetsChanged =
                                JSON.stringify(newFilters.noteTypes) !== JSON.stringify(s.clinicalFilters.noteTypes) ||
                                JSON.stringify(newFilters.formNames) !== JSON.stringify(s.clinicalFilters.formNames) ||
                                JSON.stringify(newFilters.clinicalActivities) !== JSON.stringify(s.clinicalFilters.clinicalActivities) ||
                                JSON.stringify(newFilters.assessmentScales) !== JSON.stringify(s.clinicalFilters.assessmentScales) ||
                                JSON.stringify(newFilters.servicePrograms) !== JSON.stringify(s.clinicalFilters.servicePrograms) ||
                                JSON.stringify(newFilters.serviceDescriptions) !== JSON.stringify(s.clinicalFilters.serviceDescriptions) ||
                                JSON.stringify(newFilters.providers) !== JSON.stringify(s.clinicalFilters.providers) ||
                                JSON.stringify(newFilters.caseManagers) !== JSON.stringify(s.clinicalFilters.caseManagers) ||
                                JSON.stringify(newFilters.assessmentStaff) !== JSON.stringify(s.clinicalFilters.assessmentStaff) ||
                                JSON.stringify(newFilters.dateRanges) !== JSON.stringify(s.clinicalFilters.dateRanges) ||
                                JSON.stringify(newFilters.severityIndicators) !== JSON.stringify(s.clinicalFilters.severityIndicators) ||
                                JSON.stringify(newFilters.serviceCodes) !== JSON.stringify(s.clinicalFilters.serviceCodes);

                            if (facetsChanged) {
                                s.confirmSettingChange("Filters", () => s.setClinicalFilters(newFilters));
                            } else {
                                s.setClinicalFilters(newFilters);
                            }
                        }}
                    />
                </div>
            )}

            {/* Settings change confirmation dialog */}
            <Dialog
                hidden={!s.pendingSettingChange}
                onDismiss={() => s.handleConfirmContinue()}
                dialogContentProps={{
                    type: DialogType.normal,
                    title: "Chat in Progress",
                    subText: `You're changing ${s.pendingSettingChange?.type}. Would you like to clear the current chat or continue with the existing conversation?`
                }}
                modalProps={{ isBlocking: true }}
            >
                <DialogFooter>
                    <PrimaryButton onClick={s.handleConfirmClearChat} text="Clear Chat" />
                    <DefaultButton onClick={s.handleConfirmContinue} text="Continue" />
                </DialogFooter>
            </Dialog>
        </div>
    );
};

export default ChatMobile;
